import React, { createRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { Layout } from '../common'
import OfferHeader from './offer/header'
import Descriptions from './offer/descriptions'
import { Button, Link } from '../ui'
import Infos from './offer/infos'
import OfferMap from './offer/map'
import Wrapper from '../ui/wrapper'
import OfferTools from './offer/tools'
import { Offer } from './offer/mapper'
import ButtonColor from '../ui/buttonColor'
import { useApplyStore } from '../../stores/ui/applyStore'
import { useIsVisible } from '../../Utils/useIsVisible'
import { FixedHeader } from './offer/FixedHeader'
import { OfferCTATeaser } from './OfferCTATeaser'
import { BaseMessage } from '../base/BaseMessage'
import { LinkButton } from '../common/menu/linkButton'

type OfferTemplateProps = {
  data: Offer | null
  isCareerSite: boolean
  constants: {
    siteStrapiUrl: string
    frontStrapiToken: string
    googleMapsApiKey: string | null
    recaptchaKey: string | null
  }
}

const FilledMessage = ({ positionFilled }: { positionFilled: boolean }) => {
  if (!positionFilled) {
    return null
  }
  return (
    <div className="my-4">
      <BaseMessage type="warning">
        <div className="flex items-center justify-between w-full">
          <p>Ce poste a été pourvu</p>
          <LinkButton
            href="/offres"
            label="Voir les offres"
            color="primary"
            size="small"
            isInverted
          />
        </div>
      </BaseMessage>
    </div>
  )
}

const OfferTemplate: React.FC<OfferTemplateProps> = ({
  data,
  constants,
  isCareerSite,
}) => {
  const openApplicationFormModal = useApplyStore(useShallow(state => state.openApplicationFormModal))

  if (!data) {
    return (
      <Layout
        careerSite={isCareerSite}
        apiToken={constants?.frontStrapiToken}
        apiUrl={constants?.siteStrapiUrl}
      >
        <div className="container mx-auto">
          <h1 className="text-center">Cette offre n&apos;existe pas</h1>
        </div>
      </Layout>
    )
  }

  const offerHeaderRef = createRef<HTMLDivElement>()
  const isHeaderVisible = useIsVisible(offerHeaderRef)
  const positionFilled = data.positionFilled || false

  return (
    <Layout
      careerSite={isCareerSite}
      colorCareerSite={data.office.firm.colorCareerSite || undefined}
      apiToken={constants?.frontStrapiToken}
      apiUrl={constants?.siteStrapiUrl}
    >
      <OfferHeader
        infos={data}
        careerSite={isCareerSite}
        reference={offerHeaderRef}
      />
      <FixedHeader
        data={data}
        isCareerSite={isCareerSite}
        isVisible={!isHeaderVisible}
      />


      <div className="mb-6">
        <div className="xl:w-[1115px] lg:w-[950px] lg:mx-auto 2xl:w-[1300px] mx-auto">
          <FilledMessage positionFilled={positionFilled} />
          <div className="flex flex-col lg:flex-row">
            <div className="space-y-4 offer-details lg:basis-8/12 lg:mr-6">
              <Descriptions
                descriptions={data.descriptions}
                offerMedias={data.office.firm.offerMedias}
                companyName={data.office.firm.name}
                isCareerSite={isCareerSite}
                positionFilled={positionFilled}
                colorCareerSite={data.office.firm.colorCareerSite || null}
                offerId={data.id || undefined}
              />
            </div>
            <div className="hidden text-center lg:basis-4/12 lg:block">
              {!positionFilled &&
                (isCareerSite && data.office.firm.colorCareerSite ? (
                  <ButtonColor
                    label="Postuler"
                    invert
                    color={data.office.firm.colorCareerSite || 'null'}
                    size="lg"
                    onClick={() =>
                      openApplicationFormModal({
                        offerId: data.id as string,
                        isCareerSiteApply: isCareerSite,
                        colorCareerSite:
                          data.office.firm.colorCareerSite || undefined,
                      })
                    }
                  />
                ) : (
                  <Button
                    invert
                    label="Postuler"
                    color="primary"
                    size="lg"
                    onClick={() =>
                      openApplicationFormModal({
                        offerId: data.id as string,
                      })
                    }
                  />
                ))}
              <div className="my-4">
                <Infos
                  infos={data}
                  color={
                    isCareerSite && data.office.firm.colorCareerSite
                      ? data.office.firm.colorCareerSite
                      : null
                  }
                />
              </div>
              <OfferMap
                data={data}
                apiKey={constants.googleMapsApiKey}
              />
              <Link
                to={
                  isCareerSite
                    ? `/${data?.office?.firm?.slug}`
                    : `/cabinets/${data?.office?.firm?.slug}`
                }
              >
                {isCareerSite && data.office.firm.colorCareerSite ? (
                  <ButtonColor
                    label="Voir le cabinet"
                    color={data.office.firm.colorCareerSite}
                  />
                ) : (
                  <Button label="Voir le cabinet" color="primary" />
                )}
              </Link>
            </div>
          </div>
        </div>
        <div>
          <div className="xl:w-[1115px] lg:w-[950px] mx-auto 2xl:w-[1300px">
            <div className="block lg:hidden">
              {data.tools && (
                <div className="mt-4">
                  <Wrapper>
                    <OfferTools tools={data.tools} />
                  </Wrapper>
                </div>
              )}
              <div className="mt-4">
                <Wrapper>
                  <OfferMap
                    data={data}
                    apiKey={constants.googleMapsApiKey}
                  />
                </Wrapper>
              </div>
            </div>
          </div>
        </div>
        <div className="xl:w-[1115px] lg:w-[950px] lg:mx-auto 2xl:w-[1300px] mx-auto mt-4 md:mt-6 hidden md:block">
          {positionFilled ? <FilledMessage positionFilled={positionFilled} /> : <OfferCTATeaser
            offerId={data.id as string}
            data={data}
            firmLogoUrl={data.office.firm.logo as string || undefined}
            header={data.office.firm.header as string || undefined}
            color={
              isCareerSite && data.office.firm.colorCareerSite
                ? data.office.firm.colorCareerSite
                : undefined
            }
          />}
        </div>
      </div>
    </Layout>
  )
}

export default OfferTemplate
